// Production backend: sign-in with Firebase Auth (Google, or a one-time email
// link that Firebase itself sends, from noreply@apex-minds.org, in Ukrainian),
// data and files through cabinet-api (/api/cabinet/*) on Cloud SQL and
// Cloud Storage. The same client API as devdb.js — the screens never know
// which one they talk to (api.js picks).
//
// Every request carries the Firebase ID token; cabinet-api maps it to the
// person's profile id and runs the query as them, under row-level security.
import { Query } from './query.js';

const API = '/api/cabinet';
const EMAIL_KEY = 'cabinet.signin.email';
const REDIRECT_KEY = 'cabinet.signin.google';
const LINK_PARAMS = ['mode', 'oobCode', 'apiKey', 'lang', 'continueUrl'];
// Staging only (config.json `demo: true`): signed in as a test account of the
// demo world, with a token from cabinet-api instead of Firebase (cabinet-api/demo.js).
const DEMO_KEY = 'cabinet.demo.token';

const store = {
    get: (k) => { try { return localStorage.getItem(k); } catch { return null; } },
    set: (k, v) => { try { v == null ? localStorage.removeItem(k) : localStorage.setItem(k, v); } catch {} },
};

export async function createGcpClient(cfg) {
    const fb = await import('../vendor/firebase-auth.js');
    const app = fb.initializeApp(cfg.firebase);
    const auth = fb.initializeAuth(app, {
        persistence: [fb.indexedDBLocalPersistence, fb.browserLocalPersistence],
        popupRedirectResolver: fb.browserPopupRedirectResolver,
    });
    auth.languageCode = 'uk';

    // Arriving from the sign-in email: finish signing in, then drop the
    // one-time code from the address bar. Opened on another device, the email
    // is not known here — the sign-in page asks for it (pendingLink).
    let pendingLink = null, linkError = null;
    const href = location.href;
    if (fb.isSignInWithEmailLink(auth, href)) {
        const url = new URL(href);
        LINK_PARAMS.forEach(k => url.searchParams.delete(k));
        history.replaceState(history.state, '', url.pathname + url.search + url.hash);
        const email = store.get(EMAIL_KEY);
        if (email) {
            try { await fb.signInWithEmailLink(auth, email, href); store.set(EMAIL_KEY, null); }
            catch (e) { linkError = e; }
        } else pendingLink = href;
    }
    // Back from Google: only then is there a redirect result to read (reading it
    // otherwise would load Firebase's auth iframe on every visit).
    if (store.get(REDIRECT_KEY)) {
        store.set(REDIRECT_KEY, null);
        try { await fb.getRedirectResult(auth); } catch (e) { linkError = e; }
    }
    await auth.authStateReady();

    const demoOn = !!cfg.demo;
    const demoToken = () => demoOn ? store.get(DEMO_KEY) : null;

    // ── Transport ───────────────────────────────────────────────────────
    async function call(path, { json, raw, contentType } = {}) {
        const send = async (refresh) => {
            const headers = {};
            const u = auth.currentUser;
            const dt = demoToken();
            if (dt) headers.Authorization = 'Bearer ' + dt;
            else if (u) headers.Authorization = 'Bearer ' + await u.getIdToken(refresh);
            if (json !== undefined) headers['Content-Type'] = 'application/json';
            else if (raw !== undefined) headers['Content-Type'] = contentType || raw.type || 'application/octet-stream';
            return fetch(API + path, {
                method: json === undefined && raw === undefined ? 'GET' : 'POST',
                headers, body: json !== undefined ? JSON.stringify(json) : raw, cache: 'no-store',
            });
        };
        let r = await send(false);
        // An expired demo token: signed out. An expired or revoked Firebase token: refresh once and retry.
        if (r.status === 401 && demoToken()) { store.set(DEMO_KEY, null); who = null; }
        else if (r.status === 401 && auth.currentUser) r = await send(true);
        return r;
    }
    // supabase-js style: never throws, returns { data, error }.
    async function result(path, opts) {
        try {
            const r = await call(path, opts);
            const body = await r.json().catch(() => null);
            if (r.ok && body && ('data' in body || 'error' in body)) return body;
            return { data: null, error: { message: body?.error?.message || body?.error || r.statusText || 'request failed', code: String(r.status) } };
        } catch (e) {
            return { data: null, error: { message: e.message || 'network error', code: 'NETWORK' } };
        }
    }

    // A plain route's answer: its JSON body, or { error } (never throws).
    async function answer(p) {
        try {
            const r = await p;
            const body = await r.json().catch(() => null);
            if (r.ok && body) return body;
            return { error: r.status === 404 ? 'not_found' : body?.error || r.statusText || 'request failed', status: r.status };
        } catch (e) { return { error: e.message || 'network error', status: 0 }; }
    }

    // Calls started together go out together: the /db and /rpc calls of one
    // turn of the event loop share one POST /batch (a browser sends only a few
    // requests at a time, and each is a trip through the proxy). A lone call
    // keeps its own route; an API without /batch gets them one by one again.
    const MAX_BATCH = 40;
    let queue = null, noBatch = false;
    const single = (op) => op.db ? result('/db', { json: op.db }) : result('/rpc', { json: op.rpc });
    // A message, not setTimeout: a background tab delays timers by a second.
    const soon = (() => { const ch = new MessageChannel(); let fn = null; ch.port1.onmessage = () => { const f = fn; fn = null; f(); }; return (f) => { fn = f; ch.port2.postMessage(0); }; })();
    function batched(op) {
        if (noBatch) return single(op);
        return new Promise((resolve) => {
            if (!queue) { queue = []; soon(flush); }
            queue.push({ op, resolve });
        });
    }
    function flush() {
        const all = queue; queue = null;
        if (all.length === 1) return single(all[0].op).then(all[0].resolve);
        for (let i = 0; i < all.length; i += MAX_BATCH) sendBatch(all.slice(i, i + MAX_BATCH));
    }
    async function sendBatch(part) {
        try {
            const r = await call('/batch', { json: { ops: part.map(x => x.op) } });
            if (r.status === 404) { noBatch = true; return part.forEach(x => single(x.op).then(x.resolve)); }
            const body = await r.json().catch(() => null);
            if (r.ok && Array.isArray(body?.data) && body.data.length === part.length) return part.forEach((x, i) => x.resolve(body.data[i]));
            const error = { message: body?.error?.message || body?.error || r.statusText || 'request failed', code: String(r.status) };
            part.forEach(x => x.resolve({ data: null, error }));
        } catch (e) {
            part.forEach(x => x.resolve({ data: null, error: { message: e.message || 'network error', code: 'NETWORK' } }));
        }
    }

    // ── Who am I ────────────────────────────────────────────────────────
    // /me also brings the profile and groups (cabinet-api whoami): kept for the
    // first api.me() that asks (takeBoot), never served twice.
    let who = null;   // { fbUid, user: { id, email }, boot: { profile, groups } }
    // Signed in with neither an account nor an invitation (cabinet-api answers 403
    // invite_only): the session stays, and the sign-in page offers the waiting list
    // (pages/waitlist.js). { fbUid, email, name, google }.
    let stranger = null;
    const bootOf = (me) => me && 'profile' in me && Array.isArray(me.groups) ? { profile: me.profile, groups: me.groups, courses: Array.isArray(me.courses) ? me.courses : [] } : null;
    async function currentUser() {
        const dt = demoToken();
        if (dt) {
            if (who?.demo === dt) return who.user;
            const r = await call('/me');
            if (!r.ok) { store.set(DEMO_KEY, null); who = null; return null; }
            const me = await r.json();
            who = { demo: dt, user: { id: me.id, email: me.email }, boot: bootOf(me) };
            return who.user;
        }
        const u = auth.currentUser;
        if (!u) { who = null; stranger = null; return null; }
        if (who?.fbUid === u.uid) return who.user;
        if (stranger?.fbUid === u.uid) return null;
        const r = await call('/me');
        if (r.status === 401) return null;
        if (r.status === 403 && (await r.json().catch(() => null))?.error === 'invite_only') {
            stranger = { fbUid: u.uid, email: (u.email || '').toLowerCase(), name: u.displayName || null,
                google: u.providerData.some(p => p.providerId === 'google.com') };
            return null;
        }
        if (!r.ok) throw new Error('Кабінет тимчасово недоступний. Спробуй за хвилину.');
        const me = await r.json();
        who = { fbUid: u.uid, user: { id: me.id, email: me.email }, boot: bootOf(me) };
        return who.user;
    }
    const listeners = new Set();
    const tell = (ev, session) => listeners.forEach(f => { try { f(ev, session); } catch {} });
    fb.onAuthStateChanged(auth, (u) => {
        if (!u || who?.fbUid !== u.uid) who = null;
        if (!u || stranger?.fbUid !== u.uid) stranger = null;
        listeners.forEach(f => { try { f(u ? 'SIGNED_IN' : 'SIGNED_OUT', u ? { user: u } : null); } catch {} });
    });
    const authMessage = (e) => {
        const c = e?.code || '';
        if (c.includes('invalid-action-code') || c.includes('expired-action-code')) return 'Посилання вже використане або застаріло. Надішли нове.';
        if (c.includes('invalid-email')) return 'Ця пошта не збігається з тією, на яку надіслано посилання.';
        if (c.includes('popup-closed') || c.includes('cancelled')) return 'Вхід скасовано.';
        if (c.includes('network')) return 'Немає з’єднання. Спробуй ще раз.';
        if (c.includes('too-many-requests') || c.includes('quota-exceeded')) return 'Забагато листів. Спробуй за кілька хвилин.';
        return 'Не вдалося увійти. Спробуй ще раз.';
    };

    // Every write, rpc and plain POST (some of them write) moves this count when it
    // is sent and when it is answered: what a page kept from an earlier read is
    // read afresh after one (api.js dataVersion). The visit log (`activity`, one
    // insert per page view) changes nothing a page keeps, so it does not count; nor
    // does an rpc called with { get: true } (a function that only reads).
    let writes = 0;
    const wrote = (p) => { writes++; return p.finally(() => { writes++; }); };
    return {
        from: (t) => new Query(t, (d) => d.op === 'select' || t === 'activity' ? batched({ db: d }) : wrote(batched({ db: d }))),
        rpc: (fn, args = {}, { get = false } = {}) => get ? batched({ rpc: { fn, args } }) : wrote(batched({ rpc: { fn, args } })),
        get writes() { return writes; },
        // A plain POST to the cabinet API (path under /api/cabinet): the fetch Response.
        post: (path, json) => wrote(call(path, { json })),
        // The waiting list (cabinet-api /waitlist): the stranger's own entry, and the
        // letter's link by its token (signed out). Every call answers its body, or
        // { error } — 'bad_input' | 'archived' | 'member' | 'not_found' | a network one.
        waitlist: {
            get: () => answer(call('/waitlist')),
            join: (w) => answer(call('/waitlist', { json: { op: 'join', ...w } })),
            leave: () => answer(call('/waitlist', { json: { op: 'leave' } })),
            byToken: (t) => answer(call('/waitlist/out', { json: { t } })),
            leaveByToken: (t) => answer(call('/waitlist/out', { json: { t, leave: true } })),
        },
        auth: {
            // The profile and groups that came with /me, for this user, once.
            takeBoot(id) { const b = who?.user?.id === id ? who.boot : null; if (who) who.boot = null; return b || null; },
            async getUser() { return { data: { user: await currentUser() }, error: null }; },
            async getSession() { const user = await currentUser(); return { data: { session: user ? { user } : null }, error: null }; },
            // The sign-in page: an email link opened on another device waits here
            // for the address; an email or Google sign-in that failed says why.
            pendingLink: () => !!pendingLink,
            takeError() { const e = linkError; linkError = null; return e ? authMessage(e) : null; },
            async signInWithOtp({ email, options = {} }) {
                if (pendingLink) {
                    try {
                        await fb.signInWithEmailLink(auth, email, pendingLink);
                        pendingLink = null;
                        return { data: {}, error: null, signedIn: true };
                    } catch (e) { return { data: null, error: { message: authMessage(e) } }; }
                }
                email = email.trim().toLowerCase();
                try {
                    await fb.sendSignInLinkToEmail(auth, email, { url: options.emailRedirectTo || location.origin + '/cabinet/', handleCodeInApp: true });
                } catch (e) {
                    return { data: null, error: { message: (e?.code || '').includes('invalid-email') ? 'Перевір адресу пошти.' : authMessage(e) } };
                }
                store.set(EMAIL_KEY, email);
                return { data: {}, error: null };
            },
            async signInWithOAuth({ provider }) {
                if (provider !== 'google') return { data: null, error: { message: 'unsupported provider' } };
                try {
                    const p = new fb.GoogleAuthProvider();
                    p.setCustomParameters({ prompt: 'select_account' });
                    store.set(REDIRECT_KEY, '1');
                    await fb.signInWithRedirect(auth, p);
                    return { data: {}, error: null };
                } catch (e) { return { data: null, error: { message: authMessage(e) } }; }
            },
            // Signed in, but no account and no invitation: { email, name, google } or null
            // (after api.me()).
            stranger: () => stranger && { email: stranger.email, name: stranger.name, google: stranger.google },
            async signOut() {
                who = null; stranger = null;
                if (demoToken()) { store.set(DEMO_KEY, null); tell('SIGNED_OUT', null); }
                await fb.signOut(auth);
                return { error: null };
            },
            // Staging: the test accounts and signing in as one of them.
            ...(demoOn ? {
                async demoAccounts() {
                    const r = await fetch(API + '/demo/accounts', { cache: 'no-store' });
                    if (!r.ok) throw new Error('Тестові акаунти недоступні.');
                    return (await r.json()).data || [];
                },
                async demoSignIn(email) {
                    const r = await fetch(API + '/demo/signin', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email }), cache: 'no-store' });
                    const body = await r.json().catch(() => null);
                    if (!r.ok || !body?.token) return { error: { message: 'Не вдалося увійти як тестовий користувач.' } };
                    if (auth.currentUser) await fb.signOut(auth);
                    who = null;
                    store.set(DEMO_KEY, body.token);
                    tell('SIGNED_IN', null);
                    return { error: null };
                },
            } : {}),
            onAuthStateChange(cb) { listeners.add(cb); return { data: { subscription: { unsubscribe() { listeners.delete(cb); } } } }; },
        },
        storage: {
            from(bucket) {
                const q = (path) => `?bucket=${encodeURIComponent(bucket)}&path=${encodeURIComponent(path)}`;
                return {
                    upload: (path, file, opts = {}) => result('/storage/upload' + q(path), { raw: file, contentType: opts.contentType }),
                    async createSignedUrl(path, expiresIn = 600) {
                        const r = await result('/storage/sign', { json: { bucket, paths: [path], expiresIn } });
                        const one = r.data?.[0];
                        return one?.signedUrl ? { data: { signedUrl: one.signedUrl }, error: null } : { data: null, error: r.error || { message: 'not found' } };
                    },
                    createSignedUrls: (paths, expiresIn = 600) => result('/storage/sign', { json: { bucket, paths, expiresIn } }),
                    // A photo's bytes (cabinet-api /storage/read, checked on every fetch): a Blob.
                    async download(path) {
                        try {
                            const r = await call('/storage/read', { json: { bucket, path } });
                            if (r.ok) return { data: await r.blob(), error: null };
                            const body = await r.json().catch(() => null);
                            return { data: null, error: { message: body?.error?.message || body?.error || r.statusText || 'request failed', code: String(r.status) } };
                        } catch (e) { return { data: null, error: { message: e.message || 'network error', code: 'NETWORK' } }; }
                    },
                    remove: (paths) => result('/storage/remove', { json: { bucket, paths } }),
                };
            },
        },
    };
}
